import { useApp } from "../context/AppContext";
import { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { Languages } from "lucide-react";
import { reportAPI, workQueueAPI } from "../utils/api";

const ROLE_CONFIG = {
  admin:        { color: "#4F46E5", bg: "#F1F5F9" },
  doctor:       { color: "#0D9488", bg: "#F1F5F9" },
  technician:   { color: "#2563EB", bg: "#F1F5F9" },
  receptionist: { color: "#D97706", bg: "#F1F5F9" },
};

const ROLE_LABEL_KEYS = {
  admin: "administrator", doctor: "doctor2", technician: "technician2", receptionist: "receptionist2",
};

// Minimal SVG icons — professional, no cartoon
const IcoDashboard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1.5"/>
    <rect x="14" y="3" width="7" height="7" rx="1.5"/>
    <rect x="3" y="14" width="7" height="7" rx="1.5"/>
    <rect x="14" y="14" width="7" height="7" rx="1.5"/>
  </svg>
);
const IcoPatients = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IcoReports = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);
const IcoImaging = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 3H3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);
const IcoSettings = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const IcoMonitor = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
    <polyline points="7 10 10 13 13 10 17 14"/>
  </svg>
);

const NAV = [
  { id: "dashboard", tKey: "dashboard",        icon: <IcoDashboard />, roles: ["admin","doctor","technician","receptionist"] },
  { id: "patients",  tKey: "patients",          icon: <IcoPatients />,  roles: ["admin","doctor","technician","receptionist"] },
  { id: "work-queue", tKey: "workQueue",           icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="2"/>
      <path d="M9 12h6M9 16h4"/>
    </svg>
  ), roles: ["admin","doctor","technician","receptionist"] },
  { id: "reports",   tKey: "labReports",        icon: <IcoReports />,   roles: ["admin","doctor","technician","receptionist"] },
  { id: "imaging",   tKey: "imaging",           icon: <IcoImaging />,   roles: ["admin","doctor","technician","receptionist"] },
  { id: "clinical-review", tKey: "clinicalReview", icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
      <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"/>
      <path d="M12 15.75h.007v.008H12v-.008z"/>
    </svg>
  ), roles: ["admin","doctor","technician"] },
  { id: "settings",  tKey: "settings",          icon: <IcoSettings />,  roles: ["admin", "doctor"] },
  { id: "system",    tKey: "system",            icon: <IcoMonitor />,   roles: ["admin"] },
];

// Nashman Institute — Enhanced logo matching provided brand asset
// Navy circle + white phoenix/bird + DNA helix, with gold accent bar
const NashmanLogo = ({ size = 44 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="50" fill="#1E293B"/>
    {/* Wing left */}
    <path d="M50 38 C43 31 28 28 12 33 C20 32 33 37 42 44 C45 41 47 40 50 38Z" fill="white" opacity="0.92"/>
    {/* Wing right */}
    <path d="M50 38 C57 31 72 28 88 33 C80 32 67 37 58 44 C55 41 53 40 50 38Z" fill="white" opacity="0.92"/>
    {/* Body */}
    <path d="M50 38 L47 50 L50 54 L53 50Z" fill="white"/>
    {/* Head */}
    <ellipse cx="50" cy="36" rx="4" ry="3.5" fill="white"/>
    {/* Beak */}
    <path d="M50 33 L52 35 L50 36Z" fill="#C7B47A"/>
    {/* DNA helix left strand */}
    <path d="M44 60 Q50 56 56 60" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    <path d="M44 67 Q50 63 56 67" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    <path d="M44 74 Q50 70 56 74" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    {/* DNA backbone */}
    <line x1="44" y1="60" x2="44" y2="74" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="56" y1="60" x2="56" y2="74" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    {/* DNA rungs */}
    <line x1="44" y1="63.5" x2="56" y2="63.5" stroke="white" strokeWidth="1.1" opacity="0.7"/>
    <line x1="44" y1="70.5" x2="56" y2="70.5" stroke="white" strokeWidth="1.1" opacity="0.7"/>
  </svg>
);

export default function Sidebar({ active, setActive }) {
  const { user, logout } = useApp();
  const { language, toggleLanguage, t, setActiveUsername } = useTheme();
  const rc = ROLE_CONFIG[user?.role] || {};
  const [reviewCount, setReviewCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [workQueueCount, setWorkQueueCount] = useState(0);

  // Fetch clinical review badge count — with 60s sessionStorage cache
  useEffect(() => {
    if (!user || !["admin","doctor","technician"].includes(user.role)) return;
    const CACHE_KEY = `emr_cache_clinical_review_${user.username}`;
    const CACHE_TTL = 60000; // 60 seconds

    const fetchCount = (force = false) => {
      // Show cached instantly
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, ts } = JSON.parse(cached);
          setCriticalCount(data?.critical_count || 0);
          setReviewCount(data?.total || 0);
          // If cache is fresh enough and not forced, skip network call
          if (!force && Date.now() - ts < CACHE_TTL) return;
        }
      } catch(e) {}
      // Fetch fresh
      reportAPI.getCriticalReview().then(d => {
        setCriticalCount(d?.critical_count || 0);
        setReviewCount(d?.total || 0);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: d, ts: Date.now() })); } catch(e) {}
      }).catch(() => {});
    };

    fetchCount();
    // Refresh every 2 minutes
    const interval = setInterval(() => fetchCount(true), 120000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch work queue badge count
  useEffect(() => {
    if (!user || !["admin","technician","receptionist","doctor"].includes(user.role)) return;
    const fetchWQ = () => {
      workQueueAPI.get().then(d => {
        setWorkQueueCount(d?.total_pending || 0);
      }).catch(() => {});
    };
    fetchWQ();
    const iv = setInterval(fetchWQ, 120000);
    return () => clearInterval(iv);
  }, [user]);

  // Sync language preference when user changes (login/switch role)
  // Sidebar has access to both user (AppContext) and setActiveUsername (ThemeContext)
  // so it's the right place to wire per-user language without a circular dependency.
  useEffect(() => {
    if (setActiveUsername) setActiveUsername(user?.username || null);
  }, [user?.username]);

  const visibleNav = NAV.filter(n => n.roles.includes(user?.role));

  return (
    <div className="sidebar">
      {/* Logo / Brand */}
      <div className="sidebar-logo">
        <NashmanLogo size={36} />
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#0F172A", letterSpacing: "0.2px", lineHeight: 1.2 }}>
            {t.instituteName}
          </div>
          <div style={{ fontSize: 9.5, color: "#64748B", letterSpacing: "0.3px" }}>
            {t.instituteSubtitle}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="sidebar-nav">
        {visibleNav.map(n => (
          <button
            key={n.id}
            className={`nav-item ${active === n.id ? "active" : ""} ${n.id === "clinical-review" && criticalCount > 0 ? "nav-item-alert" : ""}`}
            onClick={() => setActive(n.id)}
            style={n.id === "clinical-review" && criticalCount > 0 ? { position: "relative", animation: "navPulse 2s infinite" } : { position: "relative" }}
          >
            {n.icon}
            <span className="nav-label">{n.id === "clinical-review" ? (language === "bn" ? "ক্লিনিক্যাল রিভিউ" : "Clinical Review") : t[n.tKey]}</span>
            {n.id === "clinical-review" && reviewCount > 0 && (
              <span style={{
                position: "absolute", top: 6, right: 8,
                minWidth: 18, height: 18, borderRadius: 99,
                background: criticalCount > 0 ? "#DC2626" : "#F59E0B",
                color: "#fff", fontSize: 10, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 4px", lineHeight: 1,
                boxShadow: criticalCount > 0 ? "0 0 0 2px rgba(220,38,38,0.3)" : "none",
                animation: criticalCount > 0 ? "badgePulse 1.5s infinite" : "none"
              }}>
                {reviewCount > 99 ? "99+" : reviewCount}
              </span>
            )}
            {n.id === "work-queue" && workQueueCount > 0 && (
              <span style={{
                position: "absolute", top: 6, right: 8,
                minWidth: 18, height: 18, borderRadius: 99,
                background: "#0D9488",
                color: "#fff", fontSize: 10, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 4px", lineHeight: 1,
              }}>
                {workQueueCount > 99 ? "99+" : workQueueCount}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: rc.bg || "#EEF2FF",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: rc.color || "#4F46E5", flexShrink: 0,
          }}>
            {(user?.username || "?").substring(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t[ROLE_LABEL_KEYS[user?.role]] || user?.role}</div>
            <div style={{ fontSize: 11, color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{user?.username}</div>
          </div>
        </div>
        {/* Language toggle */}
        <div style={{ marginBottom: 8 }}>
          <button
            onClick={toggleLanguage}
            title="Switch language / ভাষা পরিবর্তন"
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
              padding: "6px 8px", borderRadius: 7, border: "1px solid var(--slate-200)",
              background: "var(--slate-100)", color: "var(--slate-600)",
              cursor: "pointer", fontSize: 11, fontWeight: 600,
            }}
          >
            <Languages size={13} />
            {language === "en" ? "বাংলা" : "English"}
          </button>
        </div>
        <button
          className="btn btn-sm"
          style={{ width: "100%", justifyContent: "center", background: "var(--slate-100)", color: "var(--slate-600)", border: "1px solid var(--slate-200)", fontSize: 12 }}
          onClick={logout}
        >
          {t.signOut}
        </button>
      </div>
    </div>
  );
}